// Shared view-model hook for the inline activity card concepts. Pulls from the
// same react-query cache as the production InlineActivityCard (same key, same
// staleTime), so rendering 4 cards for the same slug fans out to a single fetch.
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { paysByLink } from "@majourneys/shared";
import { activitiesApi } from "../../activities/api/activities.api";
import { activityKeys } from "../../activities/constants/query-keys";
import { activityHrefString } from "../../activities/lib/activity-href";

export interface ActivityBadge {
  readonly icon: string;
  readonly label: string;
}

export interface ActivityVm {
  readonly slug: string;
  readonly title: string;
  readonly image: string | undefined;
  readonly category: string;
  readonly city: string;
  readonly price: number;
  /** 0=per-person, 1=per-hour, 2=per-night, 3=per-vehicle/group. Drives the
   * price suffix: a per-vehicle activity is not priced per person. */
  readonly typePrice: number;
  readonly categorySlug: string | null;
  readonly rating: number | undefined;
  readonly description: string | null;
  readonly badges: ActivityBadge[];
  readonly href: string;
  readonly isRtl: boolean;
  /** `bookable` or `explore`. Explore rows never carry a price or a booking word. */
  readonly mode: "bookable" | "explore";
  /** A table booking: free to reserve, never priced (owner rule). */
  readonly isRestaurant: boolean;
}

export interface ActivityVmOptions {
  /** Return explore (editorial, unbookable) rows too. Off by default. */
  readonly allowExplore?: boolean;
}

export function useActivityVm(slug: string, locale = "en", options: ActivityVmOptions = {}): ActivityVm | null {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: activityKeys.detail(slug, locale),
    queryFn: () => activitiesApi.getBySlug(slug, locale),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const activity = (data as { data?: any })?.data;
  if (!activity) return null;
  const mode: "bookable" | "explore" = activity.mode === "explore" ? "explore" : "bookable";
  if (activity.mode && activity.mode !== "bookable" && !(options.allowExplore && mode === "explore")) return null;
  const isRestaurant =
    activity.template === "restaurant" ||
    activity.template === "explore-restaurant" ||
    activity.segment === "restaurants" ||
    activity.category?.slug === "restaurants";

  const title: string = activity.title;
  const subtitle: string | undefined = activity.subtitle;
  const hasRealSubtitle = !!(subtitle && subtitle !== title);

  let description: string | null = hasRealSubtitle ? subtitle! : null;
  if (!description && typeof activity.overview === "string" && activity.overview.length > 0) {
    const plain = activity.overview
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (plain.length > 0) {
      description = plain.length > 180 ? `${plain.slice(0, 177).trimEnd()}…` : plain;
    }
  }

  const badges: ActivityBadge[] = [];
  if (activity.freeCancellation) {
    badges.push({ icon: "ri-shield-check-line", label: t("activity.free_cancellation") });
  }
  // Stays and camps are paid online in full, never on arrival.
  const paidOnline = activity.template === "stay" || Number(activity.typePrice ?? 0) === 2 || paysByLink(activity.slug);
  if (activity.reservePayLater && !paidOnline) {
    badges.push({ icon: "ri-wallet-3-line", label: t("activity.pay_on_arrival") });
  }
  if (activity.whatsappConfirmation) {
    badges.push({ icon: "ri-whatsapp-line", label: t("activity.whatsapp_confirmation") });
  }

  const href = activityHrefString({
    lang: locale,
    citySlug: activity.city?.slug ?? null,
    categorySlug: activity.category?.slug ?? null,
    segment: activity.segment ?? null,
    slug: activity.slug,
  });

  return {
    slug: activity.slug,
    title,
    image: activity.images?.[0] ?? activity.heroImageUrl,
    category: activity.category?.name ?? "",
    city: activity.city?.name ?? activity.cityName ?? "",
    price: Number(activity.price ?? 0),
    typePrice: Number(activity.typePrice ?? 0),
    categorySlug: activity.category?.slug ?? null,
    // Star ratings show on stays only (owner rule); every other card hides them.
    rating: activity.template === "stay" ? activity.rating : undefined,
    description,
    badges,
    href,
    isRtl: locale === "ar",
    mode,
    isRestaurant,
  };
}
